import classNames from 'classnames'
import { Modal } from 'components/layout/Modal'
import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useState,
} from 'react'
import useTranslation from 'hooks/useTranslation'
import { isMobile } from 'react-device-detect'
import { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { Me } from 'service/user'
import { FormValues } from 'utils/type'
import { ReactComponent as StarIcon } from 'svg/star_white.svg'
import { ReactComponent as TrashIcon } from 'svg/trash.svg'
import { useInfiniteLinks } from 'hooks/query/extension/useGetLinks'
import {
  AIRecommendProductShortTitleType,
  AiThumbnailTitle,
  Link,
  LinkDetailType,
  LinkInfo,
} from 'service/list'
import useDeleteLink from 'hooks/query/extension/useDeleteLink'
import { useGetLinkDetails } from 'hooks/query/extension/useGetLinkDetail'
import InfiniteScroll from 'react-infinite-scroll-component'

import { BeatLoader } from 'react-spinners'

import { getLocalStorage } from 'utils/common'

import { UseMutateAsyncFunction } from '@tanstack/react-query'
import { MaxSelectCnt } from 'constant/videoTypes'

interface ExtensionListModalProps {
  watch: UseFormWatch<FormValues>
  setValue: UseFormSetValue<FormValues>
  control: Control<FormValues, any>
  translatePlanName?: (planName: string) => string
  userInfo: Me
  setOpenExtensionMethod: Dispatch<SetStateAction<boolean>>
  isOpenExtensionMethod: boolean
  selectedLinkIds: number[]
  setSelectedLinkIds: Dispatch<SetStateAction<number[]>>
  linkPageSize: number
  setLinkPageSize: Dispatch<SetStateAction<number>>
  linkCurrentPage: number
  setLinkCurrentPage: Dispatch<SetStateAction<number>>
  LINK_PAGE_CNT: number
  setSelectedLinks: Dispatch<SetStateAction<LinkDetailType[]>>
  isAiName: boolean
  setAiName: Dispatch<React.SetStateAction<boolean>>
  setIsOpenExtensionListFailModal: Dispatch<React.SetStateAction<boolean>>
  setAiRecommendClipTitle: Dispatch<React.SetStateAction<string[] | null>>

  setAiRecommendThumbnailTitle: Dispatch<
    SetStateAction<AiThumbnailTitle | null>
  >
  mutateAsync: UseMutateAsyncFunction<string[], Error, any, unknown>
  isPending: boolean
  thumbnailMutateAsync: UseMutateAsyncFunction<
    AiThumbnailTitle,
    Error,
    any,
    unknown
  >
  thumbnailIsPending: boolean
  createVideoType: 'long' | 'short'
}

export const ExtensionListModal = ({
  watch,
  setValue,
  control,
  translatePlanName,
  userInfo,
  setOpenExtensionMethod,
  isOpenExtensionMethod,
  selectedLinkIds,
  setSelectedLinkIds,
  linkPageSize,
  setLinkPageSize,
  linkCurrentPage,
  setLinkCurrentPage,
  LINK_PAGE_CNT,
  setSelectedLinks,
  isAiName,
  setAiName,
  setAiRecommendClipTitle,
  setAiRecommendThumbnailTitle,
  mutateAsync,
  isPending,
  thumbnailMutateAsync,
  thumbnailIsPending,
  setIsOpenExtensionListFailModal,
  createVideoType,
}: PropsWithChildren<ExtensionListModalProps>) => {
  const { t } = useTranslation()
  const [isOpenErrorModal, setOpenErrorModal] = useState<boolean>(false)
  const [isOpenDeleteModal, setOpenDeleteModal] = useState<boolean>(false)
  const [deleteId, setDeleteId] = useState<number | null>(null)

  const [linkListValue, setLinkListValue] = useState<LinkInfo | undefined>(
    undefined
  )

  const isExtensionInstalled = getLocalStorage('fs_extension')
  const [isExtension, setExtension] = useState(false)

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    isPending: linkListPending,
    refetch: linkRefetch,
  } = useInfiniteLinks(LINK_PAGE_CNT)

  const { mutate: deleteLinkMutate, isPending: deletePending } = useDeleteLink()
  const { mutateAsync: getLinkDetail } = useGetLinkDetails(selectedLinkIds)

  const handelGetLinkDetailByIds = async () => {
    try {
      const getApis = selectedLinkIds.map((id) => getLinkDetail(id))

      const results = await Promise.all(getApis)
      if (!results) setIsOpenExtensionListFailModal(true)

      const newDataArr = results.filter(
        (el): el is LinkDetailType => el !== null
      )

      setSelectedLinks((prev) => {
        // 기존 상태와 비교하여 변경된 경우에만 업데이트
        if (JSON.stringify(prev) !== JSON.stringify(newDataArr)) {
          return newDataArr
        }
        return prev // 값이 동일하면 상태 변경 X → 렌더링 방지
      })
    } catch (err) {
      setIsOpenExtensionListFailModal(true)
      console.error('에러 발생:', err)
    }
  }

  const handleModalOpen = () => {
    setOpenExtensionMethod((prev) => !prev)
  }
  const handleDeleteModal = () => {
    setOpenDeleteModal((prev) => !prev)
  }
  const handleErrorModal = () => {
    setOpenErrorModal((prev) => !prev)
  }
  const handleListStatus = (id: number) => {
    // 배열에 id가 존재하면 filter로 삭제
    const maxCnt =
      createVideoType === 'short'
        ? MaxSelectCnt.PRODUCT_SHORT
        : MaxSelectCnt.PRODUCT_LONG
    const check = selectedLinkIds.includes(id)
    if (check) {
      const newArr = selectedLinkIds.filter((num) => num !== id)
      setSelectedLinkIds(newArr)
    } else {
      const currentArr = [...selectedLinkIds, id]
      if (currentArr.length <= maxCnt) {
        setSelectedLinkIds(currentArr)
      }
    }
  }

  const handleDeleteLink = () => {
    if (deleteId) {
      deleteLinkMutate(deleteId, {
        onSuccess: () => {
          //linkRefetch()
          // refetch가아니라
          if (linkListValue) {
            const newLinkListValue = linkListValue?.data.links.filter(
              (el) => el.id !== deleteId
            )
            const newSelectedLinkIds = selectedLinkIds.filter(
              (el) => el !== deleteId
            )

            const newLinkListValueTotalCount =
              linkListValue?.data.totalCount - 1
            const newLinkListValueRemainingCapacity =
              linkListValue.data.remainingCapacity + 1

            setLinkListValue({
              data: {
                links: newLinkListValue,
                totalCount: newLinkListValueTotalCount,
                remainingCapacity: newLinkListValueRemainingCapacity,
              },
            })
            setSelectedLinkIds(newSelectedLinkIds)
            handleDeleteModal()
          }
        },
        onError: (e) => {
          console.log(e?.message)
        },
      })
    } else {
      handleDeleteModal()
    }
  }

  const handleAiRecommendClipTitle = async () => {
    const selectedIdsCnt = selectedLinkIds.length
    if (selectedIdsCnt === 0) {
      setOpenErrorModal((prev) => !prev)
    } else {
      handleModalOpen()

      const [dataResult, thumbnailResult] = await Promise.allSettled([
        mutateAsync(selectedLinkIds),
        thumbnailMutateAsync(selectedLinkIds),
      ])

      if (dataResult.status === 'fulfilled') {
        setAiRecommendClipTitle(dataResult.value ?? null)
      } else {
        setAiRecommendClipTitle(null)
      }

      if (thumbnailResult.status === 'fulfilled') {
        setAiRecommendThumbnailTitle(thumbnailResult.value ?? null)
      } else {
        setAiRecommendThumbnailTitle(null)
      }
    }
  }

  const findIcon = (shop: string) => {
    let iconPath = ''
    switch (shop) {
      case 'ali':
        iconPath = '/assets/img/ali_icon.png'
        break
      case 'coupang':
        iconPath = '/assets/img/coupang_icon.png'
        break
      case 'naver':
        iconPath = '/assets/img/naver_icon.png'
        break
      case 'smartstore':
        iconPath = '/assets/img/naver_icon.png'
        break
    }

    return iconPath
  }

  useEffect(() => {
    if (data) {
      const links = data?.pages.flatMap((page) => page.data.links) || []
      const totalCount = data?.pages.at(-1)?.data.totalCount ?? 0
      const remainingCapacity = data?.pages.at(-1)?.data.remainingCapacity ?? 0
      setLinkListValue({ data: { links, totalCount, remainingCapacity } })
      const checkList = links.filter((el) =>
        selectedLinkIds.slice(0, 3).includes(el.id)
      )
      setSelectedLinkIds(checkList.map((el) => el.id))
    }
  }, [data])

  useEffect(() => {
    setExtension(isExtensionInstalled)
  }, [isExtensionInstalled])
  useEffect(() => {
    if (isOpenExtensionMethod) {
      linkRefetch()
    }
  }, [isOpenExtensionMethod])

  const LinkRowEl = (link: Link, index: number) => {
    return (
      <div className="flex gap-2 items-center mb-2" key={`link_${index}`}>
        <div
          className={classNames(
            'rounded-[10px] flex gap-2 items-center px-2 md:py-3 py-1 w-full cursor-pointer',
            {
              'opacity-30':
                selectedLinkIds.length === 3 &&
                !selectedLinkIds.includes(link.id),
              'bg-[#BBEBEC]': selectedLinkIds.includes(link.id),
              'bg-[#F6F8FB]': !selectedLinkIds.includes(link.id),
            }
          )}
          onClick={() => {
            handleListStatus(link?.id)
          }}
        >
          <input
            type="checkbox"
            className="flex-shrink-0 w-[18px] h-[18px] appearance-none border border-gray-400 rounded-md checked:bg-main checked:border-main cursor-pointer relative 
after:content-[''] after:absolute after:w-[5px] after:h-[10px] after:border-r-2 after:border-b-2 after:border-white after:rotate-45 after:top-[2px] after:left-[5px] after:opacity-0 checked:after:opacity-100"
            checked={selectedLinkIds.includes(link.id)}
          />

          <div className="flex flex-col font-bold text-[12px] text-[#B6BBC6] flex-shrink-0">
            {String(index + 1).padStart(3, '0')}
            <div className="flex justify-end">
              <img
                src={findIcon(link.shop)}
                alt="youtube icon"
                className="w-[12px] h-[12px]"
              />
            </div>
          </div>
          <div className="flex-shrink-0">
            {' '}
            <img
              src={link?.thumbnailImage ?? ''}
              alt="youtube icon"
              className="w-[32px] h-[32px] "
            />
          </div>
          <div className="text-left">{link?.title}</div>
        </div>
        <div className="mr-4">
          <button
            onClick={() => {
              setDeleteId(link?.id)
              setOpenDeleteModal((prev) => !prev)
            }}
          >
            <TrashIcon />
          </button>
        </div>
      </div>
    )
  }

  const handleDisplayByMobile = () => {
    if (isMobile) {
      return (
        <>
          {linkListPending || isPending ? (
            <div className="overflow-auto md:h-[350px]   md:pb-4  flex items-center justify-center">
              {' '}
              <BeatLoader color="#00B7B9"></BeatLoader>
            </div>
          ) : userInfo &&
            linkListValue?.data?.totalCount &&
            linkListValue?.data?.totalCount > 0 ? (
            <>
              {' '}
              {
                <div
                  className="overflow-auto md:h-[350px] max-h-[90vh] md:pb-4 "
                  id="scroll_modal"
                >
                  <InfiniteScroll
                    dataLength={linkListValue.data.links.length} // 현재 로드된 데이터의 길이
                    next={fetchNextPage} // 데이터가 더 필요할 때 호출될 함수
                    hasMore={!!hasNextPage} // 더 불러올 데이터가 있는지 여부
                    loader={<BeatLoader color="#00B7B9"></BeatLoader>} // 로딩 중에 표시될 컴포넌트
                    endMessage={<></>} // 모든 아이템을 불러왔을 때 표시될 메시지
                    scrollableTarget="scroll_modal" // 스크롤 대상 (모달의 ID 또는 클래스를 사용)
                  >
                    {linkListValue?.data.links?.map((link, index) => (
                      <>{LinkRowEl(link, index)}</>
                    ))}
                  </InfiniteScroll>
                </div>
              }
            </>
          ) : (
            isExtension && (
              <>
                <div className="overflow-auto md:h-[350px]  md:pb-4 ">
                  <div>
                    {linkListValue?.data.totalCount === 0 && userInfo && (
                      <>
                        <p className="text-error">
                          {t('productVideoEditor.extensionListModal.noProductsMessage')}
                        </p>

                        <button className="text-main mt-6 font-bold">
                          <a
                            href="https://www.coupang.com"
                            target="_blank"
                            rel="noreferrer"
                          >
                            {t('productVideoEditor.extensionListModal.collectProducts')} {`>`}
                          </a>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </>
            )
          )}
        </>
      )
    } else {
      return (
        <>
          {/* {!isExtension && (
            <div className="md:overflow-auto md:h-[500px]  md:pb-4 ">
              <p className="text-error">
                아직 확장 프로그램을 설치하지 않았어요!
              </p>

              <button className=" mt-6 font-bold">
                <a
                  href="https://chromewebstore.google.com/detail/meeeiblldcmbdhhaibnbpdiiknkoaakl?utm_source=item-share-cb"
                  target="_blank"
                  rel="noreferrer"
                  className="flex gap-2 items-center"
                >
                  <img
                    src="assets/img/logo/extension.png"
                    alt="extension"
                    className="w-[26px] h-[26px]"
                  />
                  <span className="font-bold">
                    패스트컷 AI 확장 프로그램 설치하기 {`>`}
                  </span>{' '}
                </a>
              </button>
            </div>
          )} */}

          {linkListPending || isPending ? (
            <div className="overflow-auto md:h-[350px] md:pb-4  flex items-center justify-center">
              {' '}
              <BeatLoader color="#00B7B9"></BeatLoader>
            </div>
          ) : userInfo &&
            linkListValue?.data?.totalCount &&
            linkListValue?.data?.totalCount > 0 ? (
            <>
              {' '}
              {
                <div
                  className="overflow-auto md:h-[350px] md:max-h-[90vh] md:pb-4 "
                  id="scroll_modal"
                >
                  <InfiniteScroll
                    dataLength={linkListValue.data.links.length} // 현재 로드된 데이터의 길이
                    next={fetchNextPage} // 데이터가 더 필요할 때 호출될 함수
                    hasMore={!!hasNextPage} // 더 불러올 데이터가 있는지 여부
                    loader={<BeatLoader color="#00B7B9"></BeatLoader>} // 로딩 중에 표시될 컴포넌트
                    endMessage={<></>} // 모든 아이템을 불러왔을 때 표시될 메시지
                    scrollableTarget="scroll_modal" // 스크롤 대상 (모달의 ID 또는 클래스를 사용)
                  >
                    {linkListValue?.data.links?.map((link, index) => (
                      <>{LinkRowEl(link, index)}</>
                    ))}
                  </InfiniteScroll>
                </div>
              }
            </>
          ) : (
            <>
              <div className="md:overflow-auto md:h-[350px]  md:pb-4 ">
                <div>
                  {linkListValue?.data.totalCount === 0 && userInfo && (
                    <>
                      <p className="text-error">
                        {t('productVideoEditor.extensionListModal.noProductsMessage')}
                      </p>

                      <button className="text-main mt-6 font-bold">
                        <a
                          href="https://www.coupang.com"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t('productVideoEditor.extensionListModal.collectProducts')} {`>`}
                        </a>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )
    }
  }

  // useEffect(() => {
  //   if (isOpenExtensionMethod) {
  //     document.body.classList.add('overflow-hidden') // Tailwind 유틸리티 클래스 사용
  //   } else {
  //     document.body.classList.remove('overflow-hidden')
  //   }
  // }, [isOpenExtensionMethod])

  return (
    <>
      <Modal isModal={isOpenExtensionMethod}>
        <div className="md:h-screen h-full  w-full fixed left-0 top-0 flex flex-col justify-center items-center bg-black bg-opacity-50 text-center  z-50">
          <div
            className={classNames('', {
              'bg-white md:w-[816px] h-auto  shadow-main rounded-t-[20px] px-8 pt-8  flex flex-col   gap-8':
                !isMobile,
              'bg-white w-full h-full  shadow-main   flex flex-col   gap-8 pt-10 px-4 overflow-auto ':
                isMobile,
            })}
          >
            <div
              className={classNames('flex flex-col  gap-4 w-full ', {
                'justify-between': isMobile,
              })}
            >
              <p
                className={classNames('flex items-center', {
                  'justify-between w-full': isMobile,
                  'justify-between': !isMobile,
                })}
              >
                <span className="font-bold md:text-[24px] text-left text-[20px]">
                  {t('productVideoEditor.extensionListModal.title')}{' '}
                  <span className="text-main ml-1">
                    {linkListValue?.data.totalCount}
                  </span>
                </span>
                <button onClick={handleModalOpen}>
                  <img
                    src="assets/img/Icon_x.png"
                    alt="icon_x"
                    height={24}
                    width={24}
                  />
                </button>
              </p>
              <p
                className={classNames(' cursor-pointer', {
                  'flex items-center gap-1 p-2 bg-black rounded-[20px] text-white w-[302px]':
                    !isAiName,
                  'flex items-center gap-1 p-2 bg-[#4360EC] rounded-[20px] text-white w-[302px]':
                    isAiName,
                })}
                onClick={() => {
                  setAiName((prev) => !prev)
                }}
              >
                <input
                  type="checkbox"
                  className={classNames('', {
                    "w-[18px] h-[18px]  appearance-none border bg-white border-gray-400 rounded-md  cursor-pointer relative before:content-[''] before:absolute before:w-[5px] before:h-[10px] before:border-r-2 before:border-b-2 before:border-[#D9DEE8] before:rotate-45 before:top-[2px] before:left-[5px] before:opacity-100 mr-1":
                      !isAiName,
                    "w-[18px] h-[18px] appearance-none border border-gray-400 rounded-md checked:bg-white checked:border-[#4360EC] cursor-pointer relative after:content-[''] after:absolute after:w-[5px] after:h-[10px] after:border-r-2 after:border-b-2 after:border-[#4360EC] after:rotate-45 after:top-[2px] after:left-[5px] after:opacity-0 checked:after:opacity-100 mr-1":
                      isAiName,
                  })}
                  checked={isAiName}
                />
                <StarIcon />
                <span>{t('productVideoEditor.extensionListModal.aiRecommendOption')}</span>
              </p>
            </div>
            <div className=" ">
              {handleDisplayByMobile()}

              {isMobile && (
                <div className="pt-5 pb-20 w-[335px]">
                  <button
                    className="bg-main rounded-full w-full text-white font-bold h-[60px]"
                    onClick={() => {
                      handelGetLinkDetailByIds()
                      handleAiRecommendClipTitle()
                    }}
                    type="button"
                    disabled={isPending}
                  >
                    {t('productVideoEditor.extensionListModal.selectComplete')}{' '}
                    <span className="text-[14px] font-medium">{`(${selectedLinkIds.length}${t('common.units.count')})`}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
          {!isMobile && (
            <div
              className="relative p-8 shadow-main w-full md:w-[816px] bg-white rounded-b-[20px] 
         before:content-[''] before:absolute before:top-[-10px] before:left-0 
         before:w-full before:h-[10px] before:bg-gradient-to-t before:from-black/10 before:to-transparent z-50"
            >
              <button
                className="bg-main rounded-full w-full text-white font-bold h-[60px]"
                onClick={async () => {
                  handelGetLinkDetailByIds()
                  handleAiRecommendClipTitle()
                }}
                type="button"
                disabled={isPending}
              >
                {t('productVideoEditor.extensionListModal.selectComplete')}{' '}
                <span className="text-[14px] font-medium">{`(${selectedLinkIds.length}${t('common.units.count')})`}</span>
              </button>
            </div>
          )}
        </div>
      </Modal>
      <Modal isModal={isOpenErrorModal}>
        <div className="bg-white w-[335px] h-auto p-[20px] border-t rounded-t-[20px]">
          <div className="flex flex-col items-center justify-center h-full gap-[24px]">
            <p className="font-bold text-[18px]">
              {t('productVideoEditor.extensionListModal.errorModal.title')}
            </p>
            <div className="flex flex-col leading-[22px]">
              <p className="text-[14px] text-[#5E6068]">
                {t('productVideoEditor.extensionListModal.errorModal.description')} <br />
                <span className="text-main font-bold">
                  {t('productVideoEditor.extensionListModal.errorModal.requirement')}
                </span>
                <br />
                {t('productVideoEditor.extensionListModal.errorModal.instruction')}
              </p>
            </div>
          </div>
        </div>
        <div className="w-[335px] text-[14px]  h-[60px]  flex justify-center items-center">
          <button
            className="text-white w-[100%] h-full bg-main rounded-b-[20px] border-b border-main font-bold"
            onClick={handleErrorModal}
            type="button"
          >
            {t('common.confirm')}
          </button>
        </div>
      </Modal>
      <Modal isModal={isOpenDeleteModal}>
        {deletePending ? (
          <>
            <div className="bg-white w-[335px] h-[222px] p-[20px] border-t rounded-[20px] flex items-center justify-center flex-col gap-6">
              <p className="font-bold text-[18px]">{t('productVideoEditor.extensionListModal.deleteModal.deleting')}</p>
              <BeatLoader color="#00B7B9"></BeatLoader>
            </div>
          </>
        ) : (
          <>
            <div className="bg-white w-[335px] h-auto p-[20px] border-t rounded-t-[20px]">
              <div className="flex flex-col items-center justify-center h-full gap-[24px]">
                <p className="font-bold text-[18px]">{t('productVideoEditor.extensionListModal.deleteModal.title')}</p>
                <div className="flex flex-col leading-[22px]">
                  <p className="text-[14px] text-[#5E6068]">
                    <span className="text-main font-bold">
                      {t('productVideoEditor.extensionListModal.deleteModal.confirm')}
                    </span>
                    <br />
                    {t('productVideoEditor.extensionListModal.deleteModal.warning')}
                  </p>
                </div>
              </div>
            </div>
            <div className="w-[335px] text-[14px]  h-[60px]  flex justify-center items-center">
              <button
                className="text-[#5E6068] w-[50%] h-full bg-[#EEF1F7] rounded-bl-[20px] border-b border-[EEF1F7] font-bold"
                onClick={handleDeleteModal}
                type="button"
              >
                {t('common.cancel')}
              </button>
              <button
                className="text-white w-[50%] h-full bg-main rounded-br-[20px] border-b border-main font-bold"
                onClick={handleDeleteLink}
                type="button"
              >
                {t('common.confirm')}
              </button>
            </div>
          </>
        )}
      </Modal>
    </>
  )
}
